import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { faUndo } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@sama/app-state';
import { clamp, round, upperFirst } from '@sama/micro-dash';
import { provideValueAccessor } from '@sama/ng-core';
import { merge } from 'rxjs';
import { distinctUntilChanged, mapTo } from 'rxjs/operators';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { DebouncedEventTracker } from '../../event-tracking/debounced-event-tracker';
import { buildTrackerFromShellConnector } from '../../event-tracking/event-utils';
import { AbstractWrappedFormControl } from '../../forms/basic-inputs/abstract-wrapped-form-control';
import { KeyboardShortcutService } from '../../misc-services/keyboard-shortcut.service';

@Component({
  selector: 'h3-objectless-view-settings-slider',
  templateUrl: './objectless-view-settings-slider.component.html',
  styleUrls: ['./objectless-view-settings-slider.component.scss'],
  providers: [provideValueAccessor(ObjectlessViewSettingsSliderComponent)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ObjectlessViewSettingsSliderComponent
  extends AbstractWrappedFormControl<number>
  implements OnInit
{
  @Input({ required: true }) label!: string;
  @Input() min = 0;
  @Input() max = 100;
  @Input() valueLowerBound = 0;
  @Input() valueUpperBound = 100;
  @Input() step = 1;
  @Input() suffix = '%';
  @Input({ required: true }) default!: number;
  @Input({ required: true }) setting!: string;
  @Input({ required: true }) settingStore!: Store<number>;

  disabledTooltip: string | false = false;
  faUndo = faUndo;

  private eventTracker = new DebouncedEventTracker(
    {
      id: 'adjust_view_setting',
      bucket: 'navigation',
      sub_bucket: 'frame',
    },
    buildTrackerFromShellConnector(inject(shellConnectorToken)),
  );
  private keyboardShortcutService = inject(KeyboardShortcutService);

  override ngOnInit() {
    super.ngOnInit();

    this.eventTracker.addContext({
      method: this.setting,
      batch_included_reset: false,
    });

    this.subscribeTo(
      this.control.valueChanges.pipe(distinctUntilChanged()),
      (targetValue) => {
        const value = this.clampValue(targetValue);

        this.eventTracker.trackWithDebounce({
          source: 'mouse',
          value,
        });

        // We rely on the fact NasDirective does formControl.setValue(value,
        // {emitEvent: false}) to prevent tracking a duplicate mouse event
        this.settingStore.set(value);
      },
    );

    this.subscribeToKeyboardShortcuts();
  }

  reset(source = 'mouse') {
    if (this.control.value === this.default) {
      return;
    }
    this.eventTracker.addBatchContext({ batch_included_reset: true });

    // We rely on the fact NasDirective does formControl.setValue(value,
    // {emitEvent: false}) to prevent tracking a duplicate mouse event
    this.settingStore.set(this.default);

    this.eventTracker.trackWithDebounce({
      source,
      value: this.default,
    });
  }

  override setDisabledState(isDisabled: boolean) {
    this.disabledTooltip = isDisabled && 'Not available in this mode';
    super.setDisabledState(isDisabled);
  }

  private subscribeToKeyboardShortcuts() {
    const offset$ = merge(
      this.keyboardShortcutService
        .getMatchingEvent$('decrease' + upperFirst(this.setting))
        .pipe(mapTo(-5)),
      this.keyboardShortcutService
        .getMatchingEvent$('increase' + upperFirst(this.setting))
        .pipe(mapTo(5)),
    );

    this.subscribeTo(offset$, (offset) => {
      if (this.control.disabled) {
        return;
      }

      const value = this.clampValue(this.control.value + offset);

      // We rely on the fact NasDirective does formControl.setValue(value,
      // {emitEvent: false}) to prevent tracking an event with mouse as source
      this.settingStore.set(value);

      this.eventTracker.trackWithDebounce({
        source: 'keyboard_shortcut',
        value,
      });
    });
  }

  private clampValue(newValue: number) {
    const stepPrecision = Math.abs(Math.log10(this.step));

    const lowerBound = round(this.valueLowerBound, stepPrecision);
    const upperBound = round(this.valueUpperBound, stepPrecision);

    return clamp(newValue, lowerBound, upperBound);
  }
}
